// Spotpriser för idag och imorgon, hämtade i route loaders.
//
// Loaderdatan serialiseras in i server-HTML:en, så samma rader finns både när
// servern renderar och när klienten hydrerar. Komponenterna (via
// useSpotPrices/useAreaSpotPrices) startar från de här raderna i stället för
// en tom lista, så tabeller och nyckeltal finns i serverns HTML.
//
// `renderedAt` är tidpunkten då raderna hämtades. Den används som "nu" fram
// till att sidan monterats i webbläsaren, så att aktuell kvart/timme blir
// densamma på servern och vid hydrering (annars React #418) – även när HTML:en
// har legat en stund i kantcachen.

import { useEffect, useState } from "react";
import { useMatches } from "@tanstack/react-router";
import { supabase } from "@/integrations/supabase/client";
import type { SpotPriceRow } from "@/hooks/useSpotPrices";

export interface SpotSnapshot {
  /** Dagens datum i svensk tid (YYYY-MM-DD). */
  todayDate: string;
  today: SpotPriceRow[];
  tomorrow: SpotPriceRow[];
  /** Epoch-ms när raderna hämtades. */
  renderedAt: number;
}

const stockholmDate = new Intl.DateTimeFormat("sv-SE", {
  timeZone: "Europe/Stockholm",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/** Datum (YYYY-MM-DD) i svensk tid, med valfri förskjutning i dagar. */
export function swedishIsoDate(offsetDays = 0, nowMs = Date.now()): string {
  const today = stockholmDate.format(new Date(nowMs));
  if (!offsetDays) return today;
  // Räkna från middag UTC så att sommartid aldrig flyttar datumet.
  const d = new Date(`${today}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + offsetDays);
  return d.toISOString().slice(0, 10);
}

/** Delar upp rader på idag/imorgon (svensk tid). Ren funktion – testas separat. */
export function splitSpotRows(rows: SpotPriceRow[], nowMs: number): SpotSnapshot {
  const todayDate = swedishIsoDate(0, nowMs);
  const tomorrowDate = swedishIsoDate(1, nowMs);
  const sorted = [...rows].sort((a, b) => a.area.localeCompare(b.area));
  return {
    todayDate,
    today: sorted.filter((r) => r.price_date === todayDate),
    tomorrow: sorted.filter((r) => r.price_date === tomorrowDate),
    renderedAt: nowMs,
  };
}

/**
 * Hämtar dagens och morgondagens spotpriser. Returnerar null vid fel så att
 * sidan ändå renderas (komponenterna hämtar då själva i webbläsaren).
 */
export async function loadSpotSnapshot(): Promise<SpotSnapshot | null> {
  const now = Date.now();
  try {
    const { data, error } = await supabase
      .from("spot_prices")
      .select("price_date, area, prices, avg_price, min_price, max_price, is_complete")
      .in("price_date", [swedishIsoDate(0, now), swedishIsoDate(1, now)])
      .order("area");
    if (error) throw error;
    return splitSpotRows((data ?? []) as unknown as SpotPriceRow[], now);
  } catch (err) {
    console.error("Spotpriser kunde inte hämtas i loadern", err);
    return null;
  }
}

/** Loaderdatans nyckel. Rutter som visar spotpriser returnerar `{ spotSnapshot }`. */
export const SPOT_SNAPSHOT_KEY = "spotSnapshot";

/** Närmaste matchade rutt (inifrån och ut) som har laddat spotpriser. */
export function useSpotSeed(): SpotSnapshot | null {
  return useMatches({
    select: (matches) => {
      for (let i = matches.length - 1; i >= 0; i--) {
        const data = matches[i]?.loaderData as Record<string, unknown> | undefined;
        const seed = data?.[SPOT_SNAPSHOT_KEY] as SpotSnapshot | null | undefined;
        if (seed) return seed;
      }
      return null;
    },
  });
}

/**
 * "Nu" för prisvisning: loaderns tidpunkt under SSR och hydrering, därefter
 * webbläsarens klocka (uppdateras varje minut så att aktuell kvart byts).
 * Utan seed returneras null före montering.
 */
export function usePriceClock(seed: SpotSnapshot | null): Date | null {
  const [now, setNow] = useState<Date | null>(() => (seed ? new Date(seed.renderedAt) : null));
  useEffect(() => {
    setNow(new Date());
    const id = window.setInterval(() => setNow(new Date()), 60_000);
    return () => window.clearInterval(id);
  }, []);
  return now;
}

/**
 * Loader för rutter som visar spotpriser. Hämtar bara på servern: vid
 * klientnavigering hämtar komponenterna själva (med sessionStorage-kopian som
 * direktvisning), så att navigeringen inte väntar på databasen.
 */
export async function spotPriceLoader(): Promise<{ spotSnapshot: SpotSnapshot | null }> {
  if (typeof document !== "undefined") return { spotSnapshot: null };
  return { spotSnapshot: await loadSpotSnapshot() };
}

/** Artiklar som visar dagens spotpriser högst upp (ArticleSpotPriceCards). */
export const SPOT_PRICE_ARTICLE_PATHS: ReadonlySet<string> = new Set([
  "/blogg/hur-mycket-kostar-el-idag",
  "/blogg/vad-kostar-elen-idag",
]);
